import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Eye, Calendar, MapPin, FileText, User, FileCheck, Clock, Download } from 'lucide-react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import Pagination from '../components/Pagination';
import { ConfirmModal } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import { useNotification } from '../../sharedComponents/Notifications';
import { submissionService, eventCycleService, userService, tetfundApprovalService, Submission, RoleItem, TetfundApprovalItem } from '../services/adminAuth';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import { useAuth } from '../../../auth/hooks/auth';

// ─── Constants ──────────────────────────────────────────────────────────────────

/** Role IDs participating in the approval chain, in strict sequential order */
export const APPROVAL_ROLE_IDS: readonly number[] = [7, 8, 9, 10];

// ─── Approval Step Sub-component ────────────────────────────────────────────────

interface ApprovalStepProps {
    roleName: string;
    approverName: string | null;
    status: 'approved' | 'current' | 'pending';
    isCurrentUser: boolean;
    isLast: boolean;
}

export function ApprovalStep({ roleName, approverName, status, isCurrentUser, isLast }: ApprovalStepProps) {
    return (
        <div className="flex items-start gap-0 flex-1 min-w-0">
            <div className="flex flex-col items-center flex-1 min-w-0">
                {/* Circle */}
                <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        status === 'approved'
                            ? 'bg-green-50 border-green-500'
                            : status === 'current'
                            ? 'bg-blue-50 border-blue-500'
                            : 'bg-gray-50 border-gray-300'
                    }`}
                >
                    {status === 'approved' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : status === 'current' ? (
                        <div className="w-3 h-3 bg-blue-500 rounded-full" />
                    ) : (
                        <div className="w-3 h-3 bg-gray-300 rounded-full" />
                    )}
                </div>

                {/* Role name */}
                <p
                    className={`mt-2 text-sm font-semibold text-center leading-tight ${
                        status === 'approved'
                            ? 'text-green-700'
                            : status === 'current'
                            ? 'text-blue-700'
                            : 'text-gray-500'
                    }`}
                >
                    {roleName}
                </p>

                {/* "YOU" pill — only shown for the current user's role */}
                {isCurrentUser && (
                    <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[8px] font-bold bg-yellow-100 text-yellow-700 tracking-widest">
                        YOUR ROLE
                    </span>
                )}

                {/* Approver name */}
                <p className="text-xs text-gray-700 text-center mt-1 min-h-[1rem] truncate w-full px-1">
                    {approverName ?? ''}
                </p>
            </div>

            {/* Connector line */}
            {!isLast && (
                <div className="flex-shrink-0 w-10 mt-6">
                    <div
                        className={`h-0.5 w-full ${
                            status === 'approved' ? 'bg-green-400' : 'bg-gray-200'
                        }`}
                    />
                </div>
            )}
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────────

function ApproveSubmissions() {
    const { showNotification } = useNotification();
    const { user } = useAuth();

    // Roles + approvals — state typed directly as TetfundApprovalItem[]
    const [approvalRoles, setApprovalRoles] = useState<RoleItem[]>([]);
    const [currentApprovals, setCurrentApprovals] = useState<TetfundApprovalItem[]>([]);
    const [approvalsLoading, setApprovalsLoading] = useState(true);

    // Active cycle
    const [activeCycleId, setActiveCycleId] = useState<number | null>(null);
    const [cycleResolved, setCycleResolved] = useState(false);

    // Submissions table
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    // Details drawer
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // Approval modal
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [approving, setApproving] = useState(false);

    // ── Bootstrap ──────────────────────────────────────────────────────────────

    useEffect(() => {
        Promise.all([fetchRolesAndApprovals(), fetchActiveCycle()]);
    }, []);

    const fetchRolesAndApprovals = async () => {
        setApprovalsLoading(true);
        try {
            const [rolesRes, approvalsRes] = await Promise.all([
                userService.getAdminRoles(),
                tetfundApprovalService.getCurrentApprovals(),
            ]);

            if (rolesRes.isSuccess && rolesRes.data) {
                const ordered = APPROVAL_ROLE_IDS
                    .map(id => rolesRes.data.find(r => r.id === id))
                    .filter(Boolean) as RoleItem[];
                setApprovalRoles(ordered);
            }

            if (approvalsRes.isSuccess && Array.isArray(approvalsRes.data)) {
                setCurrentApprovals(approvalsRes.data);
            }
        } catch (err) {
            } finally {
            setApprovalsLoading(false);
        }
    };

    const fetchActiveCycle = async () => {
        try {
            const res = await eventCycleService.getActiveCycleEvent();
            setActiveCycleId(res.isSuccess && res.data?.cycle?.id ? res.data.cycle.id : null);
        } catch {
            setActiveCycleId(null);
        } finally {
            setCycleResolved(true);
        }
    };

    // ── Submissions ────────────────────────────────────────────────────────────

    const fetchSubmissions = useCallback(async () => {
        if (!activeCycleId) return;
        setTableLoading(true);
        try {
            const res = await submissionService.getAllSubmissions({
                pageNumber: currentPage,
                pageSize,
                sortBy: 'SubmittedAt',
                sortDescending: true,
                search: searchQuery,
                cycleId: activeCycleId,
                categoryId: 0,
                applicationType: 0,
                geoPoliticalZoneId: 0,
            });
            if (res.isSuccess && res.data) {
                setSubmissions(res.data.data);
                setTotalRecords(res.data.totalRecords);
                setTotalPages(res.data.totalPages);
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'Failed to fetch submissions.' });
        } finally {
            setTableLoading(false);
        }
    }, [activeCycleId, currentPage, pageSize, searchQuery]);

    useEffect(() => {
        if (!cycleResolved || !activeCycleId) return;
        const t = setTimeout(fetchSubmissions, 300);
        return () => clearTimeout(t);
    }, [cycleResolved, fetchSubmissions]);

    // ── Derived approval state ─────────────────────────────────────────────────

    const approvalMap = new Map<number, TetfundApprovalItem>(
        currentApprovals
            .filter(a => APPROVAL_ROLE_IDS.includes(a.roleId))
            .map(a => [a.roleId, a])
    );

    const myRoleIndex = APPROVAL_ROLE_IDS.findIndex(id => id === user?.roleId);
    const myRoleAlreadyApproved = myRoleIndex >= 0 && (approvalMap.get(user!.roleId)?.isApproved ?? false);

    const pendingPriorRoles =
        myRoleIndex > 0
            ? APPROVAL_ROLE_IDS.slice(0, myRoleIndex)
                  .filter(id => !approvalMap.get(id)?.isApproved)
                  .map(id => approvalRoles.find(r => r.id === id)?.name ?? `Role ${id}`)
            : [];

    const canApprove =
        myRoleIndex >= 0 &&
        !myRoleAlreadyApproved &&
        pendingPriorRoles.length === 0 &&
        !!activeCycleId;

    /** Index of the first role in the chain that has not yet approved — this is the "current" (blue) step */
    const nextPendingIndex = APPROVAL_ROLE_IDS.findIndex(id => !approvalMap.get(id)?.isApproved);

    function stepStatus(roleId: number): 'approved' | 'current' | 'pending' {
        if (approvalMap.get(roleId)?.isApproved) return 'approved';
        const idx = APPROVAL_ROLE_IDS.indexOf(roleId);
        if (idx === nextPendingIndex) return 'current';
        return 'pending';
    }

    // ── Approval submission ────────────────────────────────────────────────────

    /** Determine button text based on user's role */
    const getApprovalButtonText = (): string => {
        if (!user?.roleId) return 'Approve Submissions';

        const roleId = user.roleId;
        const roleIndex = APPROVAL_ROLE_IDS.indexOf(roleId);

        // Role ID 7 (First role - ES Registry)
        if (roleId === 7) return 'Send to ES';

        // Role ID 8 (Second role - ES)
        if (roleId === 8) return 'Send to DESS';

        // Role ID 10 (Last role - Tag Divisional Head)
        if (roleId === 10) return 'Send to Tag Chairman';

        // Role ID 9 (Third role - default case)
        return 'Approve Submissions';
    };

    const handleApprove = async () => {
        setApproving(true);
        try {
            const res = await tetfundApprovalService.bulkApproveSubmissions();
            if (res.isSuccess) {
                showNotification('success', {
                    title: 'Approved',
                    message: 'Submissions approved successfully.',
                    timer: 3000,
                });
                setConfirmOpen(false);
                fetchRolesAndApprovals();
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Approval failed.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred during approval.' });
        } finally {
            setApproving(false);
        }
    };

    // ── Table helpers ──────────────────────────────────────────────────────────

    const formatDate = (dateString: string) =>
        new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });

    const getApplicationType = (type: number) => (type === 1 ? 'Book' : 'Journal Article');

    const getStatusBadge = (status: number) => {
        const map: Record<number, { label: string; className: string }> = {
            1: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
            2: { label: 'Under Review', className: 'bg-blue-50 text-blue-700' },
            3: { label: 'Passed', className: 'bg-green-50 text-green-700' },
            4: { label: 'Failed', className: 'bg-red-50 text-red-700' },
        };
        const cfg = map[status] ?? map[1];
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${cfg.className}`}>
                {cfg.label}
            </span>
        );
    };

    const columns: TableColumn<Submission>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: v => <span className="text-gray-500 text-sm font-mono">{v}</span>,
        },
        {
            key: 'title',
            label: 'Title / Author',
            render: (_, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-1">{row.title}</div>
                    <div className="text-sm text-gray-500">{row.authors}</div>
                </div>
            ),
        },
        {
            key: 'applicationType',
            label: 'Type',
            render: v => <span className="text-gray-700">{getApplicationType(v)}</span>,
        },
        {
            key: 'category',
            label: 'Thematic Area',
            render: (_, row) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                    {row.category?.name ?? 'N/A'}
                </span>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            render: v => getStatusBadge(v),
        },
        {
            key: 'plagiarismOutcome',
            label: 'Similarity',
            render: (_, row) => (
                <span
                    className={`font-semibold ${
                        row.plagiarismOutcome >= 25
                            ? 'text-red-600'
                            : row.plagiarismOutcome >= 15
                            ? 'text-yellow-600'
                            : 'text-green-600'
                    }`}
                >
                    {row.plagiarismOutcome}%
                </span>
            ),
        },
    ];

    const actions: TableAction<Submission>[] = [
        {
            variant: 'icon',
            icon: <Eye className="w-4 h-4" />,
            onClick: (row) => {
                setSelectedSubmission(row);
                setIsDetailsOpen(true);
            },
            className: 'bg-transparent border-none py-[4px] px-[8px] cursor-pointer text-blue-500 rounded-[4px] transition-all duration-200 hover:bg-blue-50 hover:text-blue-700',
            title: 'View Details',
        },
    ];

    const emptyMessage = !cycleResolved
        ? 'Loading...'
        : !activeCycleId
        ? 'No active submission cycle.'
        : 'No submissions found.';

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">TetFund Approval</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Review the current approval chain and approve submissions for the active cycle.
                </p>
            </div>

            {/* Approval Chain Tracker */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <h2 className="text-base font-semibold text-gray-900 mb-1">Approval Process</h2>
                <p className="text-sm text-gray-500 mb-8">
                    Approvals must occur sequentially before submissions are finalised.
                </p>

                {approvalsLoading ? (
                    <div className="flex items-center justify-around animate-pulse px-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="flex flex-col items-center gap-2 flex-1">
                                <div className="w-12 h-12 rounded-full bg-gray-200" />
                                <div className="h-3 bg-gray-200 rounded w-20" />
                                <div className="h-3 bg-gray-100 rounded w-24" />
                            </div>
                        ))}
                    </div>
                ) : approvalRoles.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">Unable to load approval roles.</p>
                ) : (
                    <div className="flex items-start px-6">
                        {approvalRoles.map((role, idx) => {
                            const approval = approvalMap.get(role.id);
                            const approverName =
                                approval?.isApproved ? (approval.user?.person?.fullName ?? null) : null;
                            return (
                                <ApprovalStep
                                    key={role.id}
                                    roleName={role.name}
                                    approverName={approverName}
                                    status={stepStatus(role.id)}
                                    isCurrentUser={role.id === user?.roleId}
                                    isLast={idx === approvalRoles.length - 1}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Submissions Table */}
            <ReusableTable
                title="Active Cycle Submissions"
                subTitle="Submissions under the current active cycle"
                columns={columns}
                data={submissions}
                actions={actions}
                loading={tableLoading || !cycleResolved}
                searchPlaceholder="Search by title, author, or ID..."
                onSearch={q => {
                    setSearchQuery(q);
                    setCurrentPage(1);
                }}
                emptyMessage={emptyMessage}
                showSearch={!!activeCycleId}
                showFilter={false}
            />

            {/* Pagination */}
            {!tableLoading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={() => undefined}
                />
            )}

            {/* Approve section — only shown if current user is one of the 3 approval roles */}
            {myRoleIndex >= 0 && (
                <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">

                    {/* Already approved by this role */}
                    {myRoleAlreadyApproved && (
                        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-green-800">Already Approved</p>
                                <p className="text-sm text-green-700 mt-0.5">
                                    Your role has already approved these submissions and cannot approve again.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Waiting on prior roles */}
                    {!myRoleAlreadyApproved && pendingPriorRoles.length > 0 && (
                        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-amber-800">Approval Not Yet Available</p>
                                <p className="text-sm text-amber-700 mt-0.5">
                                    The following role{pendingPriorRoles.length > 1 ? 's' : ''} must approve before
                                    you can proceed:{' '}
                                    <span className="font-semibold">{pendingPriorRoles.join(', ')}</span>.
                                    Please check back later.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end">
                        <button
                            disabled={!canApprove}
                            onClick={() => setConfirmOpen(true)}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                                canApprove
                                    ? 'bg-green-700 text-white hover:bg-green-800'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <CheckCircle className="w-4 h-4" />
                            {getApprovalButtonText()}
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleApprove}
                title="Approve Submissions"
                message="Are you sure you want to approve these submissions? This action will mark all current cycle submissions as approved by your role."
                confirmText="Approve"
                cancelText="Cancel"
                variant="info"
                isSubmitting={approving}
            />

            {/* Details Drawer */}
            <Drawer
                isOpen={isDetailsOpen}
                onClose={() => { setIsDetailsOpen(false); setSelectedSubmission(null); }}
                title="Submission Details"
                width="max-w-3xl"
            >
                {selectedSubmission ? (
                    <div className="space-y-6">
                        {/* Badges */}
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                                {getApplicationType(selectedSubmission.applicationType)}
                            </span>
                            {getStatusBadge(selectedSubmission.status)}
                        </div>

                        {/* Title */}
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedSubmission.title}</h2>
                            <p className="text-sm text-gray-500">Submission ID: {selectedSubmission.submissionNumber}</p>
                        </div>

                        {/* Meta strip */}
                        {selectedSubmission.author?.person ? (
                            <div className="bg-gray-50 rounded-lg p-6">
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                            <Calendar className="w-4 h-4" />
                                            SUBMITTED
                                        </div>
                                        <div className="font-medium text-gray-900">{formatDate(selectedSubmission.submittedAt)}</div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                            <MapPin className="w-4 h-4" />
                                            ZONE
                                        </div>
                                        <div className="font-medium text-gray-900">
                                            {selectedSubmission.author.person.state?.geoPoliticalZone?.name || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                            <FileText className="w-4 h-4" />
                                            THEMATIC AREA
                                        </div>
                                        <div className="font-medium text-gray-900">{selectedSubmission.category?.name || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 rounded-lg p-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                            <Calendar className="w-4 h-4" />
                                            SUBMITTED
                                        </div>
                                        <div className="font-medium text-gray-900">{formatDate(selectedSubmission.submittedAt)}</div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                            <FileText className="w-4 h-4" />
                                            THEMATIC AREA
                                        </div>
                                        <div className="font-medium text-gray-900">{selectedSubmission.category?.name || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Authors */}
                        {selectedSubmission.authors && (
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                    <User className="w-5 h-5 text-gray-500" />
                                    All Authors
                                </h3>
                                <p className="text-gray-700">{selectedSubmission.authors}</p>
                            </div>
                        )}

                        {/* Abstract */}
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-gray-500" />
                                Abstract
                            </h3>
                            <p className="text-gray-700 leading-relaxed text-justify whitespace-pre-wrap">
                                {selectedSubmission.abstract}
                            </p>
                        </div>

                        {/* Additional metadata */}
                        <div className="grid grid-cols-2 gap-6 bg-white border border-gray-100 rounded-lg p-4">
                            {selectedSubmission.applicationType === 1 && selectedSubmission.edition && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Edition</h3>
                                    <p className="text-gray-900">{selectedSubmission.edition}</p>
                                </div>
                            )}
                            {selectedSubmission.applicationType === 2 && (
                                <>
                                    {selectedSubmission.volume && (
                                        <div>
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Volume</h3>
                                            <p className="text-gray-900">{selectedSubmission.volume}</p>
                                        </div>
                                    )}
                                    {selectedSubmission.issue && (
                                        <div>
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Issue</h3>
                                            <p className="text-gray-900">{selectedSubmission.issue}</p>
                                        </div>
                                    )}
                                    {selectedSubmission.publicationUrl && (
                                        <div className="col-span-full">
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Publication URL</h3>
                                            <a
                                                href={selectedSubmission.publicationUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline text-sm truncate block"
                                            >
                                                {selectedSubmission.publicationUrl}
                                            </a>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* ISBN / Keywords removed from approval details display */}

                        {/* Cycle */}
                        {selectedSubmission.cycle && (
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h3 className="font-semibold text-blue-900 mb-2">Submission Cycle</h3>
                                <p className="text-blue-800">{selectedSubmission.cycle.name}</p>
                            </div>
                        )}

                        {/* Approval Tracking */}
                        <div className="pt-4 border-t border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                Approval Tracking
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {[
                                    { label: 'Divisional Head', approved: selectedSubmission.isTagDivisionalHeadApproved },
                                    { label: 'DESS Approval', approved: selectedSubmission.isDESSApproved },
                                    { label: 'ES Approval', approved: selectedSubmission.isESApproved },
                                ].map(({ label, approved }) => (
                                    <div
                                        key={label}
                                        className={`p-3 rounded-lg border ${approved ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}
                                    >
                                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">{label}</p>
                                        <div className="flex items-center gap-1.5">
                                            {approved
                                                ? <CheckCircle size={14} className="text-green-600" />
                                                : <Clock size={14} className="text-gray-400" />}
                                            <span className={`text-xs font-semibold ${approved ? 'text-green-700' : 'text-gray-500'}`}>
                                                {approved ? 'Approved' : 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {selectedSubmission.isReadyForProcessing && (
                                <div className="mt-3 p-3 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium flex items-center gap-2">
                                    <CheckCircle size={14} />
                                    Ready for next stage of processing.
                                </div>
                            )}
                        </div>

                        {/* Action buttons */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200">
                            {false && (
                                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors">
                                    <FileCheck className="w-5 h-5" />
                                    Plagiarism Check
                                </button>
                            )}
                            <button
                                disabled={downloading}
                                onClick={async () => {
                                    if (selectedSubmission.manuscriptUrl) {
                                        setDownloading(true);
                                        const url = selectedSubmission.manuscriptUrl.startsWith('http')
                                            ? selectedSubmission.manuscriptUrl
                                            : `${FILE_BASE_URL}/${selectedSubmission.manuscriptUrl}`;

                                        const authorName = selectedSubmission.author?.person?.fullName || 'Unknown_Author';
                                        const title = selectedSubmission.title || 'Untitled';
                                        const appType = selectedSubmission.applicationType === 1 ? 'Book' : 'Journal';
                                        const filename = `${authorName}_${title}_${appType}_manuscript`
                                            .replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.pdf';

                                        try {
                                            await downloadFileFromUrl(url, filename);
                                        } catch (error) {
                                            } finally {
                                            setDownloading(false);
                                        }
                                    }
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors ${downloading ? 'opacity-75 cursor-not-allowed' : ''}`}
                            >
                                <Download className={`w-5 h-5 ${downloading ? 'animate-pulse' : ''}`} />
                                {downloading ? 'Downloading...' : 'Download Manuscript'}
                            </button>
                        </div>
                    </div>
                ) : null}
            </Drawer>
        </div>
    );
}

export default ApproveSubmissions;